import React from "react";
import NotifyButton from "./NotifyButton";
import { prefetchProfile } from "../api";
import { getVenueInfo } from "../utils/venueInfo";

// Media paths come back as "/media/..." and are served through the Nginx /api proxy,
// matching how ProfileModal resolves them.
const resolveImage = (url) =>
  !url ? null : url.startsWith("http") ? url : `/api${url}`;

// Nationality, from whichever shape this venue's stats use.
// Ginza venues emit a clean "Nationality: Japanese" line. The timetable venues glue
// the name and nationality together on the first line ("AudreyMalaysian"), so strip
// the known name off the front. Returns "" rather than guessing.
export function getNationality(entry, name) {
  const stats = (entry && entry.stats) || "";
  const labelled = stats.match(/^Nationality:\s*(.+)$/m);
  if (labelled) return labelled[1].trim();

  const firstLine = (stats.split("\n")[0] || "").trim();
  if (name && firstLine.toLowerCase().startsWith(name.toLowerCase())) {
    const rest = firstLine.slice(name.length).trim();
    // Guard against picking up stray markup or measurements.
    if (/^[A-Za-z][A-Za-z .x&/-]{1,24}$/.test(rest)) return rest;
  }
  return "";
}

// Hourly (60 min) rate. Two formats, and nothing shown if neither matches - a wrong
// price is far worse than no price.
export function getHourlyRate(entry) {
  const stats = (entry && entry.stats) || "";

  // Ginza: "Booking: 30min $190, 45min $250, 60min $300"
  const ginza = stats.match(/60\s*min\s*\$(\d{2,4})/i);
  if (ginza) return `$${ginza[1]}`;

  // Timetable: "...(Including room hire)30 / 45 / 60190 / 240 / 290"
  // Durations 30/45/60 then the three matching prices, the last being 60 min.
  const timetable = stats
    .replace(/\s+/g, " ")
    .match(/30\s*\/\s*45\s*\/\s*60\s*(\d{2,4})\s*\/\s*(\d{2,4})\s*\/\s*(\d{2,4})/);
  if (timetable) return `$${timetable[3]}`;

  return "";
}

function GalleryCard({ entry, venue, venueLabel }) {
  const original = entry.originalEntry || {};
  const images = original.images || [];
  const photo = resolveImage(images[0]);
  const nationality = getNationality(original, entry.name);
  const rate = getHourlyRate(original);

  const openProfile = (e) => {
    e.preventDefault();
    window.location.hash = `#profile-${venue}-${entry.name.replace(/[\s-]+$/, "")}`;
  };

  const timeRange = [entry.start, entry.finish].filter(Boolean).join(" – ");

  return (
    <div className="gallery-card">
      <a
        href={`#profile-${venue}-${entry.name}`}
        className="gallery-card__media"
        onClick={openProfile}
        onMouseEnter={() => prefetchProfile(entry.name, venue)}
        onFocus={() => prefetchProfile(entry.name, venue)}
        onTouchStart={() => prefetchProfile(entry.name, venue)}
        aria-label={`Open ${entry.name}'s profile`}
      >
        {photo ? (
          <img src={photo} alt={entry.name} loading="lazy" decoding="async" />
        ) : (
          <div className="gallery-card__nophoto">
            <span aria-hidden="true">📷</span>
            <span>No photo yet</span>
          </div>
        )}

        {venueLabel && <span className="gallery-card__venue">{venueLabel}</span>}

        {images.length > 1 && (
          <span className="gallery-card__count">
            <span aria-hidden="true">📷</span> {images.length}
          </span>
        )}
      </a>

      {/* Kept outside the anchor so tapping the bell doesn't also open the profile. */}
      <div className="gallery-card__bell">
        {entry.name ? <NotifyButton venue={venue} performerName={entry.name} /> : null}
      </div>

      <div className="gallery-card__body">
        <div className="gallery-card__name">
          {entry.code ? <span className="code-tag">{entry.code}</span> : null}
          <a href={`#profile-${venue}-${entry.name}`} onClick={openProfile}>
            {entry.name}
          </a>
        </div>
        {nationality && <div className="gallery-card__nat">{nationality}</div>}
        <div className="gallery-card__foot">
          <span className="gallery-card__time">{timeRange || "—"}</span>
          {rate && <span className="gallery-card__rate">{rate}/hr</span>}
        </div>
      </div>
    </div>
  );
}

// Photo-card alternative to the roster table. Same filtered/sorted entries the table
// receives, so search and sort keep working in gallery mode.
function RosterGallery({ entries, venue }) {
  const venueLabel = (getVenueInfo(venue) || {}).name || "";

  if (!entries || entries.length === 0) {
    return <p className="text-muted mb-0">No entries to show.</p>;
  }

  return (
    <div className="gallery-grid">
      {entries.map((entry) => (
        <GalleryCard
          key={entry.originalIndex}
          entry={entry}
          venue={venue}
          venueLabel={venueLabel}
        />
      ))}
    </div>
  );
}

export default RosterGallery;
